import React, {useState, useEffect} from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Card,
  CardContent,
  CardActions,
  Switch,
  FormControlLabel,
  Chip,
} from "@mui/material";
import {FeedPerformanceCalculator, FeedPerformanceData} from "../../utils/feedPerformanceCalculator";
import {
  fetchFeeds,
  Feed,
  fetchAllTemplates,
  updateTemplate,
  updateTemplateData,
  deleteTemplate,
  uploadTemplate,
  FeedPerformanceTemplate,
  FeedPerformanceTemplateData,
  UpdateTemplateDto,
  UpdateTemplateDataDto,
  createFeed,
  updateFeed,
  deleteFeed,
} from "../../api/feed";
import {
  fetchTenants,
  Tenant,
} from "../../api/tenant";
import {
  fetchFeedVendors,
  createFeedVendor,
  updateFeedVendor,
  deleteFeedVendor,
  FeedVendor,
  CreateFeedVendorDto,
  UpdateFeedVendorDto,
} from "../../api/feedVendor";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/Upload";
import AddIcon from "@mui/icons-material/Add";
import {useTranslation} from "react-i18next";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const {children, value, index, ...other} = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
      {value === index && <Box sx={{p: 3}}>{children}</Box>}
    </div>
  );
}

const TemplateManagementPage: React.FC = () => {
  const {t} = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Upload Template State
  const [feedId, setFeedId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [file, setFile] = useState<File | null>(null);

  // Template Management State
  const [templates, setTemplates] = useState<FeedPerformanceTemplate[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<FeedPerformanceTemplate | null>(null);
  const [isEditingData, setIsEditingData] = useState(false);

  // Vendor Management State
  const [vendors, setVendors] = useState<FeedVendor[]>([]);
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [editVendorId, setEditVendorId] = useState<string | null>(null);
  const [vendorForm, setVendorForm] = useState<CreateFeedVendorDto>({
    name: "",
    contactPerson: "",
    phone: "",
    email: "",
    address: "",
    website: "",
    notes: "",
    isActive: true,
  });

  // Feed Management State
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [feedDialogOpen, setFeedDialogOpen] = useState(false);
  const [editFeedId, setEditFeedId] = useState<string | null>(null);
  const [feedForm, setFeedForm] = useState({
    name: "",
    type: "grower",
    form: "pellet",
    description: "",
    quantityKg: "0",
    tenantId: "", // Empty string means "All Tenants" (null in backend)
    unitPrice: "",
    supplier: "",
    batchNumber: "",
    manufacturingDate: "",
    expiryDate: "",
    proteinContent: "",
    fatContent: "",
    fiberContent: "",
    moistureContent: "",
    recommendedFCR: "",
    isActive: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [feedsData, vendorsData, templatesData, tenantsData] = await Promise.all([
        fetchFeeds(),
        fetchFeedVendors(),
        fetchAllTemplates(),
        fetchTenants()
      ]);
      setFeeds(feedsData);
      setVendors(vendorsData);
      setTemplates(templatesData);
      setTenants(tenantsData);
    } catch (error) {
      console.error("Error loading data:", error);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Upload Template Functions
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      setFile(files[0]);
    }
  };

  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setError(null);

    if (!feedId || !vendorId || !templateName || !file) {
      setError("All fields are required.");
      return;
    }

    setLoading(true);
    try {
      // Show a more informative message for large files
      if (file.size > 1024 * 1024) {
        // Files larger than 1MB
        setMessage("Processing large file. This may take a moment...");
      }

      const result = await uploadTemplate(file, feedId, vendorId, templateName);
      setMessage(`Successfully uploaded ${result.count || result.length || 0} records`);
      setFeedId("");
      setVendorId("");
      setTemplateName("");
      setFile(null);
      loadData();
    } catch (error) {
      console.error("Upload error:", error);
      if (error instanceof Error && error.message.includes("timeout")) {
        setError("Upload timed out. The file may be too large or the server is busy. Please try again.");
      } else {
        setError("Upload failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Template Management Functions
  const handleEditTemplate = (template: FeedPerformanceTemplate) => {
    setEditingTemplate(template);
    setEditDialogOpen(true);
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;

    setLoading(true);
    try {
      // First update the basic template information
      const updatedTemplate = await updateTemplate(editingTemplate.id, {
        templateName: editingTemplate.templateName,
        description: editingTemplate.description,
        isActive: editingTemplate.isActive,
      });

      // Then update the template data if it exists
      if (editingTemplate.templateData && editingTemplate.templateData.length > 0) {
        const templateDataToUpdate: UpdateTemplateDataDto[] = editingTemplate.templateData.map((data) => ({
          id: data.id,
          doc: data.doc,
          abw: data.abw,
          fcrPercentage: data.fcrPercentage,
          feedPercentage: data.feedPercentage,
          feed1: data.feed1,
          feed2: data.feed2,
          feed3: data.feed3,
          feed4: data.feed4,
          totalFeed: data.totalFeed,
          cumulativeFeed: data.cumulativeFeed,
          biomass: data.biomass,
          count: data.count,
          finalSurvival: data.finalSurvival,
        }));

        await updateTemplateData(editingTemplate.id, templateDataToUpdate);
      }

      // Reload templates to get the updated data
      const updatedTemplates = await fetchAllTemplates();
      setTemplates(updatedTemplates);

      setEditDialogOpen(false);
      setEditingTemplate(null);
      setIsEditingData(false);
      setMessage("Template and data updated successfully");
    } catch (error) {
      console.error("Update error:", error);
      setError("Failed to update template");
    } finally {
      setLoading(false);
    }
  };

  const handleDataEdit = (rowIndex: number, field: keyof FeedPerformanceData, value: string | number) => {
    if (!editingTemplate) return;

    const numValue = typeof value === "string" ? parseFloat(value) || 0 : value;

    const updatedTemplate = FeedPerformanceCalculator.updateRowAndRecalculate(editingTemplate, rowIndex, {[field]: numValue});

    setEditingTemplate(updatedTemplate);
  };

  const handleToggleDataEdit = () => {
    setIsEditingData(!isEditingData);
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this template?")) return;

    setLoading(true);
    try {
      await deleteTemplate(id);
      setTemplates(templates.filter((t) => t.id !== id));
      setMessage("Template deleted successfully");
    } catch (error) {
      console.error("Delete error:", error);
      setError("Failed to delete template");
    } finally {
      setLoading(false);
    }
  };

  // Vendor Management Functions
  const handleAddVendor = () => {
    setEditVendorId(null);
    setVendorForm({
      name: "",
      contactPerson: "",
      phone: "",
      email: "",
      address: "",
      website: "",
      notes: "",
      isActive: true,
    });
    setVendorDialogOpen(true);
  };

  const handleEditVendor = (vendor: FeedVendor) => {
    setEditVendorId(vendor.id);
    setVendorForm({
      name: vendor.name,
      contactPerson: vendor.contactPerson,
      phone: vendor.phone,
      email: vendor.email,
      address: vendor.address,
      website: vendor.website,
      notes: vendor.notes,
      isActive: vendor.isActive,
    });
    setVendorDialogOpen(true);
  };

  const handleVendorSubmit = async () => {
    setLoading(true);
    try {
      if (editVendorId) {
        const updatedVendor = await updateFeedVendor(editVendorId, vendorForm as UpdateFeedVendorDto);
        setVendors(vendors.map((v) => (v.id === updatedVendor.id ? updatedVendor : v)));
        setMessage("Vendor updated successfully");
      } else {
        const newVendor = await createFeedVendor(vendorForm);
        setVendors([...vendors, newVendor]);
        setMessage("Vendor created successfully");
      }
      setVendorDialogOpen(false);
    } catch (error) {
      console.error("Vendor operation error:", error);
      setError("Failed to save vendor");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVendor = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this vendor?")) return;

    setLoading(true);
    try {
      await deleteFeedVendor(id);
      setVendors(vendors.filter((v) => v.id !== id));
      setMessage("Vendor deleted successfully");
    } catch (error) {
      console.error("Delete vendor error:", error);
      setError("Failed to delete vendor");
    } finally {
      setLoading(false);
    }
  };

  // Feed Management Functions
  const handleAddFeed = () => {
    setEditFeedId(null);
    setFeedForm({
      name: "",
      type: "grower",
      form: "pellet",
      description: "",
      quantityKg: "0",
      unitPrice: "",
      supplier: "",
      batchNumber: "",
      manufacturingDate: "",
      expiryDate: "",
      proteinContent: "",
      fatContent: "",
      fiberContent: "",
      moistureContent: "",
      recommendedFCR: "",
      isActive: true,
      tenantId: "", // Default to "All Tenants"
    });
    setFeedDialogOpen(true);
  };

  const handleEditFeed = (feed: Feed) => {
    setEditFeedId(feed.id);
    setFeedForm({
      name: feed.name || "",
      type: feed.type || "",
      form: feed.form || "",
      description: feed.description || "",
      quantityKg: (feed.quantityKg || 0).toString(),
      unitPrice: feed.unitPrice ? feed.unitPrice.toString() : "",
      supplier: feed.supplier || "",
      batchNumber: feed.batchNumber || "",
      manufacturingDate: feed.manufacturingDate || "",
      expiryDate: feed.expiryDate || "",
      proteinContent: feed.proteinContent ? feed.proteinContent.toString() : "",
      fatContent: feed.fatContent ? feed.fatContent.toString() : "",
      fiberContent: feed.fiberContent ? feed.fiberContent.toString() : "",
      moistureContent: feed.moistureContent ? feed.moistureContent.toString() : "",
      recommendedFCR: feed.recommendedFCR ? feed.recommendedFCR.toString() : "",
      isActive: feed.isActive !== undefined ? feed.isActive : true,
      tenantId: feed.tenantId || "",
    });
    setFeedDialogOpen(true);
  };

  const handleFeedSubmit = async () => {
    setLoading(true);
    try {
      // Convert form data to proper types before sending to API
      const processedFormData = {
        ...feedForm,
        quantityKg: parseFloat(feedForm.quantityKg as string) || 0,
        unitPrice: (feedForm.unitPrice as string) && !isNaN(parseFloat(feedForm.unitPrice as string)) ? parseFloat(feedForm.unitPrice as string) : undefined,
        proteinContent: (feedForm.proteinContent as string) && !isNaN(parseFloat(feedForm.proteinContent as string)) ? parseFloat(feedForm.proteinContent as string) : undefined,
        fatContent: (feedForm.fatContent as string) && !isNaN(parseFloat(feedForm.fatContent as string)) ? parseFloat(feedForm.fatContent as string) : undefined,
        fiberContent: (feedForm.fiberContent as string) && !isNaN(parseFloat(feedForm.fiberContent as string)) ? parseFloat(feedForm.fiberContent as string) : undefined,
        moistureContent: (feedForm.moistureContent as string) && !isNaN(parseFloat(feedForm.moistureContent as string)) ? parseFloat(feedForm.moistureContent as string) : undefined,
        recommendedFCR: (feedForm.recommendedFCR as string) && !isNaN(parseFloat(feedForm.recommendedFCR as string)) ? parseFloat(feedForm.recommendedFCR as string) : undefined,
      };

      if (editFeedId) {
        const updatedFeed = await updateFeed(editFeedId, processedFormData);
        setFeeds(feeds.map((f) => (f.id === updatedFeed.id ? updatedFeed : f)));
        setMessage("Feed updated successfully");
      } else {
        const newFeed = await createFeed(processedFormData);
        setFeeds([...feeds, newFeed]);
        setMessage("Feed created successfully");
      }
      setFeedDialogOpen(false);
    } catch (error) {
      console.error("Feed operation error:", error);
      setError("Failed to save feed");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFeed = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this feed?")) return;

    setLoading(true);
    try {
      await deleteFeed(id);
      setFeeds(feeds.filter((f) => f.id !== id));
      setMessage("Feed deleted successfully");
    } catch (error) {
      console.error("Delete feed error:", error);
      setError("Failed to delete feed");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading && !feeds.length && !vendors.length && !templates.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{width: "100%"}}>
      <Typography variant="h4" gutterBottom>
        Template Management
      </Typography>

      {message && (
        <Alert severity="success" sx={{mb: 2}} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{mb: 2}} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{borderBottom: 1, borderColor: "divider"}}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="template management tabs">
          <Tab label="Upload Template" />
          <Tab label="Manage Templates" />
          <Tab label="Vendor Management" />
          <Tab label="Feed Management" />
        </Tabs>
      </Box>

      {/* Upload Template Tab */}
      <TabPanel value={tabValue} index={0}>
        <Paper sx={{p: 3}}>
          <Typography variant="h6" gutterBottom>
            Upload New Template
          </Typography>
          <Box component="form" onSubmit={handleUploadSubmit} sx={{mt: 2}}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Vendor</InputLabel>
                  <Select value={vendorId} onChange={(e) => setVendorId(e.target.value)} label="Vendor">
                    <MenuItem value="">
                      <em>Select Vendor</em>
                    </MenuItem>
                    {vendors.map((vendor) => (
                      <MenuItem key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Feed</InputLabel>
                  <Select value={feedId} onChange={(e) => setFeedId(e.target.value)} label="Feed">
                    <MenuItem value="">
                      <em>Select Feed</em>
                    </MenuItem>
                    {feeds.map((feed) => (
                      <MenuItem key={feed.id} value={feed.id}>
                        {feed.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Template Name" value={templateName} onChange={(e) => setTemplateName(e.target.value)} required />
              </Grid>
              <Grid item xs={12} md={6}>
                <input accept=".xlsx,.xls" style={{display: "none"}} id="file-upload" type="file" onChange={handleFileChange} />
                <label htmlFor="file-upload">
                  <Button variant="outlined" component="span" fullWidth>
                    {file ? file.name : "Choose Excel File"}
                  </Button>
                </label>
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || !feedId || !vendorId || !templateName || !file}
                  startIcon={<UploadIcon />}>
                  {loading ? "Uploading..." : "Upload Template"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </TabPanel>

      {/* Manage Templates Tab */}
      <TabPanel value={tabValue} index={1}>
        <Paper sx={{p: 3}}>
          <Typography variant="h6" gutterBottom>
            Manage Templates
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Template Name</TableCell>
                  <TableCell>Feed</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Data Records</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell>{template.templateName}</TableCell>
                    <TableCell>{feeds.find((f) => f.id === template.feedId)?.name || "Unknown"}</TableCell>
                    <TableCell>{vendors.find((v) => v.id === template.vendorId)?.name || "Unknown"}</TableCell>
                    <TableCell>{template.templateData?.length || 0} records</TableCell>
                    <TableCell>
                      <Chip label={template.isActive ? "Active" : "Inactive"} color={template.isActive ? "success" : "default"} size="small" />
                    </TableCell>
                    <TableCell>{new Date(template.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditTemplate(template)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteTemplate(template.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      {/* Vendor Management Tab */}
      <TabPanel value={tabValue} index={2}>
        <Paper sx={{p: 3}}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Vendor Management</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddVendor}>
              Add Vendor
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Contact Person</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vendors.map((vendor) => (
                  <TableRow key={vendor.id}>
                    <TableCell>{vendor.name}</TableCell>
                    <TableCell>{vendor.contactPerson}</TableCell>
                    <TableCell>{vendor.phone}</TableCell>
                    <TableCell>{vendor.email}</TableCell>
                    <TableCell>
                      <Switch
                        checked={vendor.isActive}
                        onChange={(e) => setVendors(vendors.map((v) => (v.id === vendor.id ? {...v, isActive: e.target.checked} : v)))}
                        name="isActive"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditVendor(vendor)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteVendor(vendor.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      {/* Feed Management Tab */}
      <TabPanel value={tabValue} index={3}>
        <Paper sx={{p: 3}}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Feed Management</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddFeed}>
              Add Feed
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Form</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Quantity (kg)</TableCell>
                  <TableCell>Unit Price</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {feeds.map((feed) => (
                  <TableRow key={feed.id}>
                    <TableCell>{feed.name}</TableCell>
                    <TableCell>{feed.type}</TableCell>
                    <TableCell>{feed.form}</TableCell>
                    <TableCell>{feed.supplier}</TableCell>
                    <TableCell>{feed.quantityKg}</TableCell>
                    <TableCell>${feed.unitPrice}</TableCell>
                    <TableCell>
                      <Switch
                        checked={feed.isActive}
                        onChange={(e) => setFeeds(feeds.map((f) => (f.id === feed.id ? {...f, isActive: e.target.checked} : f)))}
                        name="isActive"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditFeed(feed)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteFeed(feed.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      {/* Template Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Edit Template: {editingTemplate?.templateName}</DialogTitle>
        <DialogContent>
          {editingTemplate && (
            <Box>
              {/* Template Metadata */}
              <Typography variant="h6" gutterBottom>
                Template Information
              </Typography>
              <Grid container spacing={2} sx={{mb: 3}}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Template Name"
                    value={editingTemplate.templateName}
                    onChange={(e) => setEditingTemplate({...editingTemplate, templateName: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={editingTemplate.description || ""}
                    onChange={(e) => setEditingTemplate({...editingTemplate, description: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editingTemplate.isActive}
                        onChange={(e) => setEditingTemplate({...editingTemplate, isActive: e.target.checked})}
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                </Grid>
              </Grid>

              {/* Template Data Records */}
              {editingTemplate.templateData && editingTemplate.templateData.length > 0 ? (
                <>
                  <Box sx={{mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                    <Typography variant="h6">Template Data Records ({editingTemplate.templateData?.length || 0})</Typography>
                    <Button variant={isEditingData ? "contained" : "outlined"} onClick={handleToggleDataEdit} color="primary">
                      {isEditingData ? "View Mode" : "Edit Mode"}
                    </Button>
                  </Box>
                  <TableContainer component={Paper} sx={{maxHeight: 400}}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>S.No</TableCell>
                          <TableCell>DOC</TableCell>
                          <TableCell>ABW</TableCell>
                          <TableCell>FCR %</TableCell>
                          <TableCell>Feed %</TableCell>
                          <TableCell>Feed 1</TableCell>
                          <TableCell>Feed 2</TableCell>
                          <TableCell>Feed 3</TableCell>
                          <TableCell>Feed 4</TableCell>
                          <TableCell>Total Feed</TableCell>
                          <TableCell>Cumulative Feed</TableCell>
                          <TableCell>Biomass</TableCell>
                          <TableCell>Count</TableCell>
                          <TableCell>Survival %</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {editingTemplate.templateData?.map((data, index) => (
                          <TableRow key={data.id || index}>
                            <TableCell>{index}</TableCell>
                            <TableCell>
                              {isEditingData ? (
                                <TextField
                                  size="small"
                                  value={data.doc || ""}
                                  onChange={(e) => handleDataEdit(index, "doc", parseInt(e.target.value) || 0)}
                                  type="number"
                                  sx={{width: 60}}
                                  inputProps={{
                                    style: {fontSize: "0.75rem"},
                                  }}
                                />
                              ) : (
                                data.doc
                              )}
                            </TableCell>
                            <TableCell>
                              {isEditingData ? (
                                <TextField
                                  size="small"
                                  value={data.abw || ""}
                                  onChange={(e) => handleDataEdit(index, "abw", parseFloat(e.target.value) || 0)}
                                  type="number"
                                  inputProps={{
                                    step: 0.01,
                                    style: {fontSize: "0.75rem"},
                                  }}
                                  sx={{width: 80}}
                                />
                              ) : typeof data.abw === "number" ? (
                                data.abw.toFixed(2)
                              ) : (
                                data.abw || "N/A"
                              )}
                            </TableCell>
                            <TableCell>
                              {isEditingData ? (
                                <TextField
                                  size="small"
                                  value={data.fcrPercentage || ""}
                                  onChange={(e) => handleDataEdit(index, "fcrPercentage", parseFloat(e.target.value) || 0)}
                                  type="number"
                                  inputProps={{
                                    step: 0.01,
                                    style: {fontSize: "0.75rem"},
                                  }}
                                  sx={{width: 80}}
                                />
                              ) : typeof data.fcrPercentage === "number" ? (
                                data.fcrPercentage.toFixed(2)
                              ) : (
                                data.fcrPercentage || "N/A"
                              )}
                            </TableCell>
                            <TableCell>
                              {isEditingData ? (
                                <TextField
                                  size="small"
                                  value={data.feedPercentage || ""}
                                  onChange={(e) => handleDataEdit(index, "feedPercentage", parseFloat(e.target.value) || 0)}
                                  type="number"
                                  inputProps={{
                                    step: 0.01,
                                    style: {fontSize: "0.75rem"},
                                  }}
                                  sx={{width: 80}}
                                />
                              ) : typeof data.feedPercentage === "number" ? (
                                data.feedPercentage.toFixed(2)
                              ) : (
                                data.feedPercentage || "N/A"
                              )}
                            </TableCell>
                            <TableCell>{typeof data.feed1 === "number" ? data.feed1.toFixed(2) : data.feed1 || "N/A"}</TableCell>
                            <TableCell>{typeof data.feed2 === "number" ? data.feed2.toFixed(2) : data.feed2 || "N/A"}</TableCell>
                            <TableCell>{typeof data.feed3 === "number" ? data.feed3.toFixed(2) : data.feed3 || "N/A"}</TableCell>
                            <TableCell>{typeof data.feed4 === "number" ? data.feed4.toFixed(2) : data.feed4 || "N/A"}</TableCell>
                            <TableCell>{typeof data.totalFeed === "number" ? data.totalFeed.toFixed(2) : data.totalFeed || "N/A"}</TableCell>
                            <TableCell>
                              {typeof data.cumulativeFeed === "number" ? data.cumulativeFeed.toFixed(2) : data.cumulativeFeed || "N/A"}
                            </TableCell>
                            <TableCell>{typeof data.biomass === "number" ? data.biomass.toFixed(2) : data.biomass || "N/A"}</TableCell>
                            <TableCell>
                              {isEditingData ? (
                                <TextField
                                  size="small"
                                  value={data.count || ""}
                                  onChange={(e) => handleDataEdit(index, "count", parseInt(e.target.value) || 0)}
                                  type="number"
                                  sx={{width: 80}}
                                  inputProps={{
                                    style: {fontSize: "0.75rem"},
                                  }}
                                />
                              ) : (
                                data.count || "N/A"
                              )}
                            </TableCell>
                            <TableCell>
                              {typeof data.finalSurvival === "number" ? data.finalSurvival.toFixed(2) : data.finalSurvival || "N/A"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Typography color="text.secondary">No data records found for this template.</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateTemplate} variant="contained" disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vendor Dialog */}
      <Dialog open={vendorDialogOpen} onClose={() => setVendorDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editVendorId ? "Edit Vendor" : "Add Vendor"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{mt: 1}}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={vendorForm.name}
                onChange={(e) => setVendorForm({...vendorForm, name: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Person"
                value={vendorForm.contactPerson}
                onChange={(e) => setVendorForm({...vendorForm, contactPerson: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Phone" value={vendorForm.phone} onChange={(e) => setVendorForm({...vendorForm, phone: e.target.value})} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={vendorForm.email}
                onChange={(e) => setVendorForm({...vendorForm, email: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={vendorForm.address}
                onChange={(e) => setVendorForm({...vendorForm, address: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Website"
                value={vendorForm.website}
                onChange={(e) => setVendorForm({...vendorForm, website: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={vendorForm.isActive}
                    onChange={(e) => setVendorForm({...vendorForm, isActive: e.target.checked})}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={vendorForm.notes}
                onChange={(e) => setVendorForm({...vendorForm, notes: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVendorDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleVendorSubmit} variant="contained" disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feed Dialog */}
      <Dialog open={feedDialogOpen} onClose={() => setFeedDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{editFeedId ? "Edit Feed" : "Add Feed"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{mt: 1}}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Name" value={feedForm.name} onChange={(e) => setFeedForm({...feedForm, name: e.target.value})} required />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={feedForm.type} onChange={(e) => setFeedForm({...feedForm, type: e.target.value})} label="Type">
                  <MenuItem value="starter">Starter</MenuItem>
                  <MenuItem value="grower">Grower</MenuItem>
                  <MenuItem value="finisher">Finisher</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Form</InputLabel>
                <Select value={feedForm.form} onChange={(e) => setFeedForm({...feedForm, form: e.target.value})} label="Form">
                  <MenuItem value="crumble">Crumble</MenuItem>
                  <MenuItem value="pellet">Pellet</MenuItem>
                  <MenuItem value="powder">Powder</MenuItem>
                  <MenuItem value="natural">Natural</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Supplier"
                value={feedForm.supplier}
                onChange={(e) => setFeedForm({...feedForm, supplier: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Quantity (kg)"
                type="number"
                value={feedForm.quantityKg}
                onChange={(e) => setFeedForm({...feedForm, quantityKg: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Unit Price"
                type="number"
                value={feedForm.unitPrice}
                onChange={(e) => setFeedForm({...feedForm, unitPrice: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Batch Number"
                value={feedForm.batchNumber}
                onChange={(e) => setFeedForm({...feedForm, batchNumber: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Manufacturing Date"
                type="date"
                value={feedForm.manufacturingDate}
                onChange={(e) => setFeedForm({...feedForm, manufacturingDate: e.target.value})}
                InputLabelProps={{shrink: true}}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={feedForm.expiryDate}
                onChange={(e) => setFeedForm({...feedForm, expiryDate: e.target.value})}
                InputLabelProps={{shrink: true}}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch checked={feedForm.isActive} onChange={(e) => setFeedForm({...feedForm, isActive: e.target.checked})} color="primary" />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tenant Assignment</InputLabel>
                <Select
                  value={feedForm.tenantId}
                  onChange={(e) => setFeedForm({...feedForm, tenantId: e.target.value})}
                  label="Tenant Assignment"
                >
                  <MenuItem value="">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      🌐 All Tenants (Global Feed)
                    </Box>
                  </MenuItem>
                  {tenants.map((tenant) => (
                    <MenuItem key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Protein Content (%)"
                type="number"
                value={feedForm.proteinContent}
                onChange={(e) => setFeedForm({...feedForm, proteinContent: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Fat Content (%)"
                type="number"
                value={feedForm.fatContent}
                onChange={(e) => setFeedForm({...feedForm, fatContent: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Fiber Content (%)"
                type="number"
                value={feedForm.fiberContent}
                onChange={(e) => setFeedForm({...feedForm, fiberContent: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Moisture Content (%)"
                type="number"
                value={feedForm.moistureContent}
                onChange={(e) => setFeedForm({...feedForm, moistureContent: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Recommended FCR"
                type="number"
                value={feedForm.recommendedFCR}
                onChange={(e) => setFeedForm({...feedForm, recommendedFCR: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={feedForm.description}
                onChange={(e) => setFeedForm({...feedForm, description: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeedDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleFeedSubmit} variant="contained" disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateManagementPage;
